import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../App";
import Layout from "../components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, TrendingUp, Copy, UserCheck, Bot, History, CheckSquare, ArrowUp, ArrowDown, DollarSign, UserPlus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { Link, useSearchParams } from 'react-router-dom';
import { VibeBotCard } from '../components/social/VibeBotCard';
import { CopiedPortfolioSummary } from '../components/social/CopiedPortfolioSummary';
import { CopiedTradeHistory } from '../components/social/CopiedTradeHistory';
import { BotFeeNotice } from '../components/social/BotFeeNotice';
import { CopyTierNotice } from '../components/social/CopyTierNotice';
import { apiError } from '../lib/apiError';
import '../styles/watchlist-paper.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mini Performance Chart Component
const MiniPerformanceChart = ({ data, isPositive = true }) => {
  if (!data || data.length === 0) return null;
  
  const chartData = data.map(item => ({ value: item.value }));
  const color = isPositive ? '#10b981' : '#ef4444';
  
  return (
    <div className="w-24 h-12">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData}>
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke={color} 
            strokeWidth={1.5} 
            dot={false}
            animationDuration={1000}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

function SocialTrading() {
  const [params] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [traders, setTraders] = useState([]);
  const [aiBots, setAiBots] = useState([]);
  const [copiedTrades, setCopiedTrades] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tradersError, setTradersError] = useState(false);
  const [botsError, setBotsError] = useState(false);
  const [botsLoading, setBotsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(['traders', 'aibots', 'portfolio'].includes(params.get('tab')) ? params.get('tab') : 'traders');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [selectedTrader, setSelectedTrader] = useState(null);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [copyAccess, setCopyAccess] = useState(null);
  const [accessError, setAccessError] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    fetchTraders();
    fetchAiBots();
    fetchCopiedTrades();
    fetchCopyAccess();
    const refresh = () => { fetchCopyAccess(); fetchAiBots(); };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);

  const fetchTraders = async () => {
    setLoading(true); setTradersError(false);
    try {
      const response = await axios.get(`${API}/traders`);
      setTraders(response.data);
    } catch (error) {
      setTraders([]); setTradersError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchCopyAccess = async () => {
    try { const { data } = await axios.get(`${API}/copy-trading/access`); setCopyAccess(data); setAccessError(false); }
    catch { setCopyAccess(null); setAccessError(true); }
  };

  const fetchAiBots = async () => {
    setBotsLoading(true); setBotsError(false);
    try {
      const response = await axios.get(`${API}/ai-bots`);
      setAiBots(response.data);
    } catch (error) {
      setAiBots([]); setBotsError(true);
    } finally {
      setBotsLoading(false);
    }
  };

  const fetchCopiedTrades = async () => {
    setHistoryLoading(true);
    setHistoryError(false);
    try {
      const response = await axios.get(`${API}/portfolio/copied-trades`);
      setCopiedTrades(response.data);
    } catch (error) {
      console.error("Error fetching copied trades:", error);
      setHistoryError(true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleFollow = async (traderId, isFollowing, autoCopyEnabled, isBot = false) => {
    try {
      if (isFollowing) {
        await axios.delete(`${API}/follow/${traderId}`);
        toast.success(`Unfollowed ${isBot ? 'AI bot' : 'trader'}`);
      } else {
        if (isBot && !copyAccess?.bot_copy_allowed) { toast.error('Premium is required to follow AI bots.'); return; }
        setTermsAgreed(false);
        // Show terms modal for new follows
        setSelectedTrader({ id: traderId, isBot });
        setShowTermsModal(true);
        return;
      }
      
      if (isBot) {
        await fetchAiBots();
      } else {
        await fetchTraders();
      }
      await fetchCopiedTrades();
    } catch (error) {
      toast.error(apiError(error, 'Action failed')); fetchCopyAccess();
    }
  };

  const confirmFollow = async () => {
    if (followBusy) return;
    if (!termsAgreed || !selectedTrader) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    setFollowBusy(true);
    try {
      await axios.post(`${API}/follow`, {
        following_id: selectedTrader.id,
        auto_copy_enabled: true,
        terms_agreed: true
      });
      
      toast.success(selectedTrader.isBot ? 'AI bot followed. Percentage-sizing preference saved; live wallet execution remains locked.' : 'Trader followed. Copy preferences saved.');
      
      if (selectedTrader.isBot) {
        await fetchAiBots();
      } else {
        await fetchTraders();
      }
      await fetchCopiedTrades();
      
      setShowTermsModal(false);
      setSelectedTrader(null);
      setTermsAgreed(false);
    } catch (error) {
      toast.error(apiError(error, 'Action failed')); fetchCopyAccess(); fetchAiBots();
    } finally {
      setFollowBusy(false);
    }
  };

  const toggleAutoCopy = async (traderId) => {
    try {
      await axios.post(`${API}/follow`, {
        following_id: traderId,
        auto_copy_enabled: true
      });
      toast.success("Auto-copy settings updated");
      await fetchTraders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="social-trading-container">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-100 mb-2 flex items-center">
            <Users className="w-8 h-8 mr-3 text-cyan-400" />
            Social Trading
          </h1>
          <p className="text-slate-400" data-testid="social-trading-scope">Follow traders and AI research activity. Bot following does not authorize on-chain wallet execution.</p>
        </div>

        <CopyTierNotice access={copyAccess} error={accessError} />

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border-cyan-800/50 p-6">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
              <Copy className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-100 mb-1">{activeTab === 'aibots' ? 'Paper and auto-copy activity' : 'How Auto-Copy Works'}</h3>
              <p className="text-slate-300 text-sm">
                {activeTab === 'aibots' ? 'Vibe Research AI governs eligible paper entries. Unattended live execution remains locked; its permission design, independent review and explicit activation are still required. Other bots’ displayed statistics are demo data, not verified live fills.' : "Recorded trader copies can include legacy 10% trader and 2% platform commission fields. These records do not prove on-chain fee transfers. Vibe paper activity is separate."}
              </p>
              {activeTab === 'aibots' && <BotFeeNotice />}
            </div>
          </div>
        </Card>

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="social-tabs grid w-full grid-cols-3 bg-slate-800 border-slate-700">
            <TabsTrigger data-testid="social-tab-traders" value="traders" className="text-slate-300 data-[state=active]:bg-cyan-600 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Top Traders
            </TabsTrigger>
            <TabsTrigger data-testid="social-tab-aibots" value="aibots" className="text-slate-300 data-[state=active]:bg-cyan-600 data-[state=active]:text-white">
              <Bot className="w-4 h-4 mr-2" />
              AI Bots
            </TabsTrigger>
            <TabsTrigger data-testid="social-tab-portfolio" value="portfolio" className="text-slate-300 data-[state=active]:bg-cyan-600 data-[state=active]:text-white">
              <History className="w-4 h-4 mr-2" />
              My Portfolio
            </TabsTrigger>
          </TabsList>

          {/* Top Traders Tab */}
          <TabsContent value="traders" className="space-y-6">
            {tradersError ? <div role="alert" data-testid="trader-catalog-error" className="text-red-300 py-5">Traders could not load. <Button variant="outline" onClick={fetchTraders} data-testid="trader-catalog-retry">Retry</Button></div> : traders.length === 0 ? (
              <Card className="bg-slate-800/50 border-slate-700 p-12 text-center">
                <Users className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No traders found. Be the first to start trading!</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {traders.map(trader => (
                  <Card key={trader.id} className="bg-slate-800/50 border-slate-700 p-6 hover:bg-slate-800/70 transition-all duration-300" data-testid={`trader-card-${trader.id}`}>
                    <div className="flex items-center space-x-4 mb-4">
                      <Avatar className="w-16 h-16">
                        <AvatarImage src={trader.picture} alt={trader.name} />
                        <AvatarFallback className="bg-cyan-600 text-white text-lg">
                          {trader.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0 [overflow-wrap:anywhere]">
                        <h3 className="text-lg font-bold text-slate-100">{trader.name}</h3>
                        <p className="text-slate-400 text-sm">{trader.total_trades} trades</p>
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 text-sm">Portfolio Value</span>
                        <span className="text-cyan-400 font-bold" data-testid={`trader-portfolio-${trader.id}`}>
                          ${trader.portfolio_value.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 text-sm">Win Rate</span>
                        <span className={`font-bold ${trader.win_rate >= 60 ? 'text-emerald-400' : trader.win_rate >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {trader.win_rate}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 text-sm">Copy Traders</span>
                        <div className="flex items-center space-x-1">
                          <UserPlus className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-300 text-sm">{trader.follower_count || 0}</span>
                        </div>
                      </div>
                      
                      {/* 7-Day Performance Chart */}
                      {trader.performance_data && (
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 text-sm">7-Day Performance</span>
                          <div className="flex items-center space-x-2">
                            <MiniPerformanceChart 
                              data={trader.performance_data} 
                              isPositive={trader.performance_data[trader.performance_data.length - 1]?.value >= trader.performance_data[0]?.value}
                            />
                            <span className={`text-xs font-semibold ${
                              trader.performance_data[trader.performance_data.length - 1]?.value >= trader.performance_data[0]?.value 
                                ? 'text-emerald-400' : 'text-red-400'
                            }`}>
                              {trader.performance_data.length > 0 && 
                                `${(trader.performance_data[trader.performance_data.length - 1]?.value - trader.performance_data[0]?.value).toFixed(1)}%`
                              }
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {trader.is_following && (
                        <Badge className="bg-emerald-600 text-white w-full justify-center" data-testid={`following-badge-${trader.id}`}>
                          <UserCheck className="w-4 h-4 mr-1" />
                          Following
                        </Badge>
                      )}
                    </div>

                    <div className="space-y-2">
                      {!trader.is_following ? (
                        <Button
                          onClick={() => handleFollow(trader.id, false, false)}
                          className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white border-0"
                          data-testid={`follow-button-${trader.id}`}
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Follow & Auto-Copy
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleFollow(trader.id, true, trader.auto_copy_enabled)}
                          variant="outline"
                          className="w-full bg-slate-700 hover:bg-slate-600 border-slate-600 text-slate-200"
                          data-testid={`unfollow-button-${trader.id}`}
                        >
                          Unfollow
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* AI Bots Tab */}
          <TabsContent value="aibots" className="space-y-6">
            {botsError ? <div role="alert" data-testid="bot-catalog-error" className="text-red-300 py-5">AI bots could not load. <Button variant="outline" onClick={fetchAiBots} data-testid="bot-catalog-retry">Retry</Button></div> : botsLoading ? (
              <Card className="bg-slate-800/50 border-slate-700 p-12 text-center">
                <Bot className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">AI bots are loading...</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {aiBots.map(bot => bot.paper_only ? <VibeBotCard key={bot.id} bot={bot} onChange={fetchAiBots} /> : (
                  <Card key={bot.id} className="bg-slate-800/50 border-slate-700 p-6 hover:bg-slate-800/70 transition-all duration-300" data-testid={`ai-bot-card-${bot.id}`}>
                    <div className="flex items-center space-x-4 mb-4 min-w-0">
                      <Avatar className="w-16 h-16">
                        <AvatarImage src={bot.avatar_url} alt={bot.name} />
                        <AvatarFallback className="bg-purple-600 text-white text-lg">
                          <Bot className="w-8 h-8" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0 [overflow-wrap:anywhere]">
                        <h3 className="text-lg font-bold text-slate-100">{bot.name}</h3>
                        <p className="text-slate-400 text-sm">{bot.strategy}</p>
                        <span className="text-xs text-amber-300" data-testid={`legacy-bot-demo-${bot.id}`}>Demo statistics</span>
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      <p className="text-slate-300 text-sm">{bot.description}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 text-sm">Win Rate</span>
                        <span className={`font-bold ${bot.win_rate >= 70 ? 'text-emerald-400' : bot.win_rate >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {bot.win_rate}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-400 text-sm">Copy Traders</span>
                        <div className="flex items-center space-x-1">
                          <UserPlus className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-300 text-sm">{bot.follower_count || 0}</span>
                        </div>
                      </div>
                      
                      {/* 7-Day Performance Chart */}
                      {bot.performance_data && (
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 text-sm">7-Day Performance</span>
                          <div className="flex items-center space-x-2">
                            <MiniPerformanceChart 
                              data={bot.performance_data} 
                              isPositive={bot.performance_data[bot.performance_data.length - 1]?.value >= bot.performance_data[0]?.value}
                            />
                            <span className={`text-xs font-semibold ${
                              bot.performance_data[bot.performance_data.length - 1]?.value >= bot.performance_data[0]?.value 
                                ? 'text-emerald-400' : 'text-red-400'
                            }`}>
                              {bot.performance_data.length > 0 && 
                                `${(bot.performance_data[bot.performance_data.length - 1]?.value - bot.performance_data[0]?.value).toFixed(1)}%`
                              }
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {bot.is_following && (
                        <Badge className="bg-purple-600 text-white w-full justify-center" data-testid={`following-bot-badge-${bot.id}`}>
                          <UserCheck className="w-4 h-4 mr-1" />
                          Following AI Bot
                        </Badge>
                      )}
                    </div>

                    <div className="space-y-2">
                      {!bot.is_following && !(copyAccess?.bot_copy_allowed && bot.can_follow) ? (
                        <Button asChild variant="outline" className="w-full text-cyan-300 border-cyan-800"><Link to="/join" data-testid={`upgrade-bot-button-${bot.id}`}>Premium required · $9.99/month</Link></Button>
                      ) : !bot.is_following ? (
                        <Button
                          onClick={() => handleFollow(bot.id, false, false, true)}
                          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-0"
                          data-testid={`follow-bot-button-${bot.id}`}
                        >
                          <Bot className="w-4 h-4 mr-2" />
                          Follow AI Bot
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleFollow(bot.id, true, bot.auto_copy_enabled, true)}
                          variant="outline"
                          className="w-full bg-slate-700 hover:bg-slate-600 border-slate-600 text-slate-200"
                          data-testid={`unfollow-bot-button-${bot.id}`}
                        >
                          Unfollow Bot
                        </Button>
                      )}
                      {bot.is_following && !bot.new_copies_allowed && <p className="text-xs text-amber-300" data-testid={`bot-copy-paused-${bot.id}`}>{!bot.can_follow ? 'New bot copies are paused without Premium. You can still unfollow.' : 'Auto-copy is turned off in your saved preferences.'}</p>}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Portfolio Tab */}
          <TabsContent value="portfolio" className="space-y-6">
            <CopiedPortfolioSummary />
            {aiBots.some(bot => bot.paper_only && bot.is_following) && <div className="paper-following-summary" data-testid="portfolio-vibe-following"><span>Following Vibe paper activity · Separate virtual ledger, no wallet copies</span><Link data-testid="portfolio-vibe-details" to="/social-trading/vibe-bot">View paper results →</Link></div>}
            <div>
              <h2 className="text-base md:text-lg font-bold text-slate-100 mb-4" data-testid="copied-trades-heading">My Portfolio - Copied Trades</h2>
              <CopiedTradeHistory trades={copiedTrades} loading={historyLoading} error={historyError} onRetry={fetchCopiedTrades} />
            </div>
          </TabsContent>
        </Tabs>

        {/* Terms Agreement Modal */}
        <Dialog open={showTermsModal} onOpenChange={open => { if (!followBusy) setShowTermsModal(open); }}>
          <DialogContent data-testid="copy-follow-dialog" closeTestId="copy-follow-close" className="bg-slate-800 border-slate-700 text-slate-100 w-[calc(100%-2rem)] max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-cyan-400">Terms and Conditions</DialogTitle>
              <DialogDescription className="text-slate-300">
                Please read and agree to the following terms before following this {selectedTrader?.isBot ? 'AI bot' : 'trader'}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-slate-700/50 p-4 rounded-lg text-sm text-slate-300">
                <h4 className="font-semibold text-slate-200 mb-2">Important Disclaimer:</h4>
                <ul className="space-y-2 text-xs">
                  <li>• Trading cryptocurrency involves substantial risk and may result in significant losses.</li>
                  <li>• Past performance does not guarantee future results.</li>
                  <li>• Superior Traders Group is not liable for any losses incurred from copied trades.</li>
                  <li data-testid="social-follow-fee-terms">{selectedTrader?.isBot ? '• Bot fee policy: 8% of positive realized net profit only, after confirmed closure and costs. Collection is not enabled. Paper activity is exempt, and following does not authorize wallet orders or transfers.' : '• Legacy trader-copy records may include 10% trader and 2% platform commissions; these are not proof of on-chain transfers.'}</li>
                  <li>• You are responsible for your own investment decisions and risk management.</li>
                  <li>• Only invest what you can afford to lose.</li>
                  {selectedTrader?.isBot && <li data-testid="bot-percentage-terms">• Bot copies use the bot’s allocation percentage against your own available funds, not its full order. Exits use your copied holdings. Premium is required; live automation is not yet available.</li>}
                </ul>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="terms" 
                  data-testid="copy-follow-terms"
                  checked={termsAgreed} 
                  onCheckedChange={checked => setTermsAgreed(checked === true)}
                  className="border-slate-500"
                />
                <label htmlFor="terms" className="text-sm text-slate-300 cursor-pointer">
                  I have read and agree to these terms and conditions
                </label>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                data-testid="copy-follow-cancel"
                disabled={followBusy}
                onClick={() => setShowTermsModal(false)}
                className="flex-1 bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600"
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmFollow}
                data-testid="copy-follow-confirm"
                disabled={!termsAgreed || followBusy}
                className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 disabled:opacity-50"
              >
                {followBusy ? 'Saving…' : selectedTrader?.isBot ? 'Follow AI Bot' : 'Follow & Auto-Copy'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default SocialTrading;